import React from "react";
import { v4 as uuidv4 } from "uuid";

import NodeDrawing from "./NodeDrawing";
import LinkDrawing from "./LinkDrawing";
import { FADE_COLOR, COLORS } from "../../constants";
import { AnswersProps } from "./types";
import Node from "../../models/node";
import { NodeLink } from "../../types";
import { AnswerType } from "../../types/answers";
import { useSelector } from "react-redux";
import { answersSelector } from "../../store/selectors";

const Answers: React.FC<AnswersProps> = ({ graph, isExpanded }) => {
  const drawFadedNodes = (
    drawnNodes: Node[],
    nodeDom: React.ReactElement[]
  ): void => {
    const { nodeArray } = graph;

    nodeArray.forEach((node) => {
      const drawn = drawnNodes.some((n) => n.name === node.name);
      if (!drawn) {
        nodeDom.unshift(
          <NodeDrawing
            isExpanded={isExpanded}
            key={node.name}
            node={node}
            color={FADE_COLOR}
            notClickable
          />
        );
      }
    });
  };

  const drawFadedLinks = (
    drawnLinks: NodeLink[],
    linkDom: React.ReactElement[]
  ): void => {
    const { linkArray } = graph;

    linkArray.forEach((link) => {
      const drawn = drawnLinks.some(
        (lnk) =>
          lnk.start.name === link.start.name && lnk.end.name === link.end.name
      );
      if (!drawn) {
        linkDom.unshift(
          <LinkDrawing
            isExpanded={isExpanded}
            key={`${link.start.name}->${link.end.name}`}
            link={link}
            color={FADE_COLOR}
            notClickable
          />
        );
      }
    });
  };

  const answers = useSelector(answersSelector);

  const drawAnswerNode = (
    node: Node,
    color: string,
    nodeDom: React.ReactElement[],
    drawnNodes: Node[]
  ): void => {
    nodeDom.push(
      <NodeDrawing
        key={uuidv4()}
        node={node}
        color={color}
        notClickable
        isExpanded={isExpanded}
      />
    );
    drawnNodes.push(node);
  };

  const drawAnswerLink = (
    prev: Node,
    current: Node,
    color: string,
    drawnLinks: NodeLink[],
    linkDom: React.ReactElement[]
  ): void => {
    const { linkArray } = graph;

    const link = linkArray.find(
      (lnk) => lnk.start.name === prev.name && lnk.end.name === current.name
    );

    if (!link) return;

    linkDom.unshift(
      <LinkDrawing
        key={uuidv4()}
        link={link}
        color={color}
        notClickable
        isExpanded={isExpanded}
      />
    );
    drawnLinks.push(link);
  };

  const drawAnswer = (
    answer: AnswerType,
    color: string,
    drawnNodes: Node[],
    drawnLinks: NodeLink[],
    nodeDom: React.ReactElement[],
    linkDom: React.ReactElement[]
  ): void => {
    answer.path.forEach((node, index) => {
      drawAnswerNode(node, color, nodeDom, drawnNodes);
      if (index === 0) return;

      const prev = answer.path[index - 1];
      drawAnswerLink(prev, node, color, drawnLinks, linkDom);
    });
  };

  const drawAnswers = (
    drawnNodes: Node[],
    drawnLinks: NodeLink[],
    nodeDom: React.ReactElement[],
    linkDom: React.ReactElement[]
  ): void => {
    answers.forEach((answer, j) => {
      const color = COLORS[j];
      if (!answer.path) return;
      drawAnswer(answer, color, drawnNodes, drawnLinks, nodeDom, linkDom);
    });
  };

  const answersDOM = (
    linkDom: React.ReactElement[],
    nodeDom: React.ReactElement[]
  ): React.ReactElement[] => {
    return [...linkDom, ...nodeDom];
  };

  const renderAnswers = (): React.ReactElement[] => {
    const drawnNodes: Node[] = [];
    const drawnLinks: NodeLink[] = [];
    const nodeDom: React.ReactElement[] = [];
    const linkDom: React.ReactElement[] = [];
    drawAnswers(drawnNodes, drawnLinks, nodeDom, linkDom);
    drawFadedNodes(drawnNodes, nodeDom);
    drawFadedLinks(drawnLinks, linkDom);

    return answersDOM(linkDom, nodeDom);
  };
  return answers ? <React.Fragment>{renderAnswers()}</React.Fragment> : null;
};

export default Answers;
